import { EditorProps } from "@grapesjs/react";
import grapesjs, { Block, Editor, EditorConfig } from "grapesjs";
import { useEffect, useRef, useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	TextField,
} from "@mui/material";
import { ImageArea } from "mdi-material-ui";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import { RootState } from "../../app/store";
import CustomDesignedEditor from "../../components/GrapesJS/CustomDesignedEditor";
import HeaderThemeSelect from "../../components/header-theme-select";
import HeaderTemplateImporter from "../../components/HeaderImporter";
import ImagePopUpV3 from "../../components/ImagePopUpV3";
import PopupWrapper from "../../components/PopupWrapper";
import { SaveHeaderModal } from "../../components/SaveHeaderModal";
import { HtmlEditor } from "../../components/SimpleCodeEditor";
import TabSwitch from "../../components/tab-switch-component";
import {
	setGlobalPopupElement,
	setGlobalProductPopupElement,
	setLoading,
} from "../../features/popupSlice";
import { fetchImagesModule } from "../../modules/mediaManager";
import { CustomComponentSettingsFunction } from "../../settings";
import { customFormType, formSelectHandler } from "../../settings/customForm";
import {
	menuComponentType,
	menuSelectHandler,
} from "../../settings/menuItemsComponent";
import { CustomHeaderItem } from "../../types/header";
import { getImageUrl, isSuperShop, toastMe } from "../../utils";
import { CustomLandingItem } from "../../utils/custom-landing";
import { customLandingWidgets } from "../../utils/customLandingWidgets";
import {
	headerComponents,
	headerLayoutComponents,
	searchBarComponents,
	socialButtons,
} from "../../utils/header-builder";
import landingPages from "../../utils/landingPages";
import {
	createGlobalHeader,
	getCustomHeader,
	getGlobalHeader,
	updateCustomHeader,
	updateGlobalHeader,
} from "../../utils/requests";
import { showToast } from "../HomePage";

declare global {
	interface Window {
		grapesjs: typeof grapesjs;
	}
}

export interface ExtraEditorProps {
	publishPage?: () => void;
	exportJson?: () => void;
}

export const defaultOptions: EditorConfig = {
	storageManager: false,
	height: "100vh",
	assetManager: {
		// assets: [],
		// custom: true,
		upload: false,
		autoAdd: false,
	},
	undoManager: {
		trackSelection: false,
	},
	selectorManager: {
		componentFirst: true,
	},
	style: landingPages.styles.content,
	// components: landingPages.landingPage1.content,
};

const initialData = {
	components: [],
	// style: landingPages.styles.content,
	// style: [
	// 	{
	// 		selectors: ["#ih4y"],
	// 		style: {
	// 			padding: "10px",
	// 		},
	// 	},
	// ],
};

// Simulate an asynchronous API call with pagination
const fetchAssets = async (page: number, limit = 10): Promise<any[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await fetchImagesModule({
				currentPage: page,
				lastPage: 10,
				total: 100,
			});

			resolve(res?.data || []);
		} catch (error) {
			reject(error);
		}
	});
};

export const plugins: EditorProps["plugins"] = [
	{
		id: "gjs-blocks-basic",
		src: "https://unpkg.com/grapesjs-blocks-basic",
	},
	"grapesjs-plugin-forms",
	"grapesjs-component-countdown",
	{
		id: "grapesjs-tailwind",
		src: "https://unpkg.com/grapesjs-tailwind",
	},
	// {
	// 	id: "grapesjs-preset-webpage",
	// 	src: "https://unpkg.com/grapesjs-preset-webpage",
	// },
];

const widgetName = "header-widget";

const HeaderBuilder = () => {
	const dispatch = useDispatch();
	const shop = useSelector((state: RootState) => state.auth.shop);

	const navigate = useNavigate();
	const { pageTitle: headerId } = useParams();

	const queryParams = new URLSearchParams(window.location.search);
	const themeUpdate = queryParams.get("themeUpdate") === "true";

	const [header, setHeader] = useState<CustomHeaderItem | null>(null);
	const [theme, setTheme] = useState<CustomLandingItem | null>(null);
	const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
	const [editorSettings, setEditorSettings] = useState<{
		[key: string]: any;
	}>({});

	// const [selectedComponent, setSelectedComponent] = useState<any>(null);
	const selectedComponent = useRef<any>(null);

	useEffect(() => {
		if (headerId && editorInstance) {
			const queryParams = new URLSearchParams(window.location.search);
			const themeUpdate = queryParams.get("themeUpdate") === "true";

			if (themeUpdate) {
				dispatch(setLoading(true));

				getGlobalHeader(headerId)
					.then((res) => {
						setHeader(res.data);

						if (editorInstance) {
							const components = res.data?.settings?.content;
							const style = res.data?.settings?.style;

							editorInstance.setComponents(components);
							editorInstance.setStyle(style);
						}
					})
					.finally(() => {
						dispatch(setLoading(false));
					});
			} else {
				dispatch(setLoading(true));

				getCustomHeader(headerId)
					.then((res) => {
						setHeader(res.data);

						if (editorInstance) {
							const components = res.data?.settings?.content;
							const style = res.data?.settings?.style;

							editorInstance.setComponents(components);
							editorInstance.setStyle(style);
						}
					})
					.finally(() => {
						dispatch(setLoading(false));
					});
			}
		}
	}, [headerId, dispatch, editorInstance]);

	const onEditor = (editor: Editor) => {
		// editor.setComponents(initialData.components);
		// editor.setStyle(landingPages.styles.content);

		const addCustomWidget = (
			editor: Editor,
			data: {
				className: string;
				content: string;
				label: string;
				media: string;
				category: string;
				customSettings?: CustomComponentSettingsFunction;
			}
		) => {
			data.customSettings?.(
				editor,
				data.className,
				data.content,
				{
					data: editorSettings,
					setData: setEditorSettings,
				},
				{
					dispatch: dispatch,
				}
			);

			// const cssComposer = editor.CssComposer;
			// const sm = editor.SelectorManager;
			// var c1 = sm.add(data.className);

			// const rule = cssComposer.add([c1]);

			// rule.set("style", {});

			editor.BlockManager.add(data.className, {
				label: data.label,
				media: data.media,
				// content: data.content,
				content: {
					type: data.className, // Directly specify the custom component type
					components: data.content,
				},
				category: data.category,
				// attributes: {
				// 	class: data.className,
				// 	title: data.label,
				// 	input: "text",
				// },
			});
		};

		headerLayoutComponents.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Header Layouts",
				className: widget.key,
				content: widget.content,
				label: widget.name,
				media: widget.media,
			});
		});

		headerComponents.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Header Components",
				className: widget.key,
				content: widget.content,
				label: widget.name,
				media: widget.media,
			});
		});

		searchBarComponents.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Search Bar",
				className: widget.key,
				content: widget.content,
				label: widget.name,
				media: widget.media,
			});
		});

		socialButtons.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Social Buttons",
				className: widget.key,
				content: widget.content,
				label: widget.name,
				media: widget.media,
			});
		});

		customLandingWidgets.forEach((widget) => {
			addCustomWidget(editor, {
				category: "Sections",
				className: widget.key,
				content: widget.comp,
				label: widget.name,
				media: widget.media || widget.comp,
				customSettings: widget.customComponentSettings,
			});
		});

		const blocks = editor.BlockManager.getAll();

		blocks.each((block) => {
			const media = block.get("label");

			// Check if media is an inline SVG
			if (media && media.startsWith("<svg")) {
				block.set("media", media); // Update media source to a proper URL
				block.set("label", "");
			}
		});

		// Listen for component selection events
		editor.on("component:selected", (model) => {
			const type = model.get("type");
			console.log(
				"Selected Component Type:",
				model.get("attributes")?.id
			);
			if (type === "image") {
				// setSelectedComponent(model); // Set the selected image component
				selectedComponent.current = model;
			} else if (model.get("attributes")?.id === menuComponentType) {
				menuSelectHandler(dispatch, model);
			} else if (model.get("attributes")?.id === customFormType) {
				formSelectHandler(dispatch, model);
			} else {
				// setSelectedComponent(null);
				selectedComponent.current = null;
			}
		});

		editor.on("component:add", (component) => {
			// Check if it's a specific component type (like a section or checkout)
			if (component.get("type") === "checkout-section") {
				console.log("Checkout Section Added!", component);
				// Perform additional actions if needed
				// If you want to ensure sections only go to the root
				const wrapper = editor.getWrapper();
				if (component.parent() !== wrapper) {
					console.log("Moving to Root...");
					// wrapper?.remove(component);
					wrapper?.append(component);

					// Remove the component from its current parent
					// component.parent()?.remove(component);
				}
			} else if (component.get("tagName") === "button") {
				const attrs = component.getAttributes();

				// Only replace if button type is not "submit"
				if (attrs.type !== "submit") {
					component.set("tagName", "a");
					component.set("attributes", {
						...attrs,
						href: "#", // Default href
					});

					// Add default traits of <a> manually
					component.set("traits", [
						{ type: "text", label: "Href", name: "href" },
						{ type: "text", label: "Title", name: "title" },
						{
							type: "select",
							label: "Target",
							name: "target",
							options: [
								{ value: "_self", name: "Self" },
								{ value: "_blank", name: "New Window" },
								{ value: "_parent", name: "Parent" },
								{ value: "_top", name: "Top" },
							],
						},
					]);
				}
			} else if (component.get("tagName") === "a") {
				// Add default traits of <a> manually
				component.set("traits", [
					{ type: "text", label: "Href", name: "href" },
					{ type: "text", label: "Title", name: "title" },
					{
						type: "select",
						label: "Target",
						name: "target",
						options: [
							{ value: "_self", name: "Self" },
							{ value: "_blank", name: "New Window" },
							{ value: "_parent", name: "Parent" },
							{ value: "_top", name: "Top" },
						],
					},
				]);
			}
			// else if (component.get("attributes")?.id === menuComponentType) {
			//     const menuId = findMenuId(component);

			//     console.log({ menuId, component });
			// }
		});

		// Add the anchor button to the block manager
		editor.BlockManager.add("anchor-button", {
			label: "Anchor Button",
			category: "Basic",
			media: "<div class='anchor-button' style='display: inline-block; width: 100%; font-size: 18px; padding: 10px 20px; text-decoration: none; color: white; background-color: blue; border-radius: 4px; text-align: center;'>BUTTON</div>",
			content:
				"<a href='#' style='display: inline-block; padding: 10px 20px; text-decoration: none; color: white; background-color: blue; border-radius: 4px; text-align: center;'>Anchor Button</a>",
		});

		// Override the default asset manager command
		editor.Commands.add("open-assets", {
			run(editor, sender) {
				dispatch(
					setGlobalPopupElement(
						<ImagePopUpV3
							onClose={() =>
								dispatch(setGlobalPopupElement(null))
							}
							onUpdate={(images) => {
								if (selectedComponent) {
									selectedComponent.current?.set(
										"attributes",
										{
											...selectedComponent.current.get(
												"attributes"
											),
											src: getImageUrl(
												images?.[0]?.path || ""
											),
										}
									);

									selectedComponent.current?.set(
										"src",
										getImageUrl(images?.[0]?.path || "")
									);
								}
								dispatch(setGlobalPopupElement(null));
							}}
						/>
					)
				);
				// setShowImagePopup(true); // Show the custom ImagePopup
				try {
					sender?.set("active", false); // Turn off the default asset manager behavior
				} catch {}
			},
		});

		editor?.Commands.add("core:code-editor", {
			run(editor) {
				dispatch(
					setGlobalPopupElement(
						<PopupWrapper bgClass="bg-slate-800">
							<HtmlEditor
								htmlValue={editor.getHtml()}
								cssValue={editor.getCss()}
								onUpdate={(code: any) => {
									const { html, css } = code;
									editor?.setComponents(html);
									editor?.setStyle(css);

									dispatch(setGlobalPopupElement(null));
								}}
							/>
						</PopupWrapper>
					)
				);
			},
		});

		// Add the custom import template command
		editor?.Commands.add("core:import-template", {
			run(editor) {
				dispatch(
					setGlobalPopupElement(
						<PopupWrapper>
							<TabSwitch
								tabs={[
									{
										id: "select-theme",
										title: "Select Theme",
										component: (
											<HeaderThemeSelect
												onSelectHeader={(header) => {
													const content =
														header.settings
															?.content;
													const style =
														header.settings?.style;
													editor.setComponents(
														content
													);
													editor.setStyle(style);

													dispatch(
														setGlobalPopupElement(
															null
														)
													);
												}}
												onEditHeader={(headerId) => {
													navigate(
														`/header-builder/${headerId}?themeUpdate=true`
													);

													dispatch(
														setGlobalPopupElement(
															null
														)
													);
												}}
											/>
										),
									},
									{
										id: "import",
										title: "Import",
										component: (
											<HeaderTemplateImporter
												onImport={(html) => {
													editor.setComponents(html);

													dispatch(
														setGlobalPopupElement(
															null
														)
													);
												}}
											/>
										),
									},
								]}
								defaultTabId="select-theme"
							/>
						</PopupWrapper>
					)
				);
			},
		});

		setEditorInstance(editor); // Save the editor instance
	};

	const addBlockToRootLayout = (block: Block) => {
		if (!editorInstance) return;

		const wrapper = editorInstance.getWrapper();
		if (!wrapper) return;

		const component = editorInstance.Components.addComponent({
			type: block.id,
		});

		if (component) {
			wrapper.append(component);
			editorInstance.select(component); // Optionally select the added component
		}
	};

	const getExportedJson = () => {
		if (editorInstance) {
			const content = editorInstance.getHtml();
			const style = editorInstance.getCss();

			return {
				content,
				style: `
					${style}
					.object-cover {
						filter: none !important;
					}
				`,
			};
		} else {
			return null;
		}
	};

	const publishPage = () => {
		const data = {
			settings: getExportedJson(),
		};

		if (data && headerId) {
			updateCustomHeader(headerId, data)
				.then((res) => {
					// toastMe("Header updated!");
					swal("Good job!", "Header updated!", "success");
				})
				.catch((err) => {
					// toastMe("Header updating page!", "error");
					swal("Failed!", "Footer update failed!", "success");
				});
		}
	};

	const updateThemeInfo = () => {
		dispatch(
			setGlobalPopupElement(
				<SaveHeaderModal
					header={header || undefined}
					isOpen={true}
					onClose={() => dispatch(setGlobalPopupElement(null))}
					onSave={(data) => {
						if (!header?._id) return Promise.reject();

						return new Promise((resolve, reject) => {
							updateGlobalHeader(header?._id, {
								name: data.name,
								images: data.image,
								description: data.description,
								settings: header?.settings,
							})
								.then((res) => {
									setHeader(res.data);
									// showToast("Theme Info Updated");
									swal(
										"Good job!",
										"Theme Info Updated",
										"success"
									);
								})
								.catch(() => {
									toastMe(
										"Failed to update theme info",
										"error"
									);
								});

							dispatch(setGlobalPopupElement(null));
						});
					}}
				/>
			)
		);
	};

	// Save theme globally
	const saveThemeGlobal = () => {
		const exportedData = getExportedJson();
		if (exportedData) {
			const handleClose = () => {
				dispatch(setGlobalPopupElement(null));
			};

			const handleSubmit = (data: {
				name: string;
				image: string;
				description?: string;
			}): Promise<CustomHeaderItem> => {
				return new Promise((resolve, reject) => {
					createGlobalHeader({
						name: data.name,
						images: data.image,
						description: data.description,
						settings: exportedData,
					})
						.then((res) => {
							showToast("Added to global Themes");
							resolve(res.data);
						})
						.catch(() => {
							toastMe("Failed to add to global themes", "error");
							reject(false);
						});
				});
			};

			dispatch(
				setGlobalPopupElement(
					<SaveHeaderModal
						header={header || undefined}
						isOpen={true}
						onClose={handleClose}
						onSave={handleSubmit}
					/>
				)
			);
		}
	};

	const updateThemeGlobal = () => {
		const exportedData = getExportedJson();
		if (exportedData) {
			const handleClose = () => {
				dispatch(setGlobalPopupElement(null));
			};

			const handleSubmit = (data: {
				name: string;
				image: string;
				description?: string;
			}): Promise<CustomHeaderItem> => {
				if (!header?._id) return Promise.reject();

				return new Promise((resolve, reject) => {
					updateGlobalHeader(header?._id, {
						name: data.name,
						images: data.image,
						description: data.description,
						settings: exportedData,
					})
						.then((res) => {
							showToast("Added to global Themes");
							resolve(res.data);
						})
						.catch(() => {
							toastMe("Failed to add to global themes", "error");
							reject(false);
						});
				});
			};

			if (!!header) {
				handleSubmit({
					name: header?.name,
					image: header?.images,
					description: header?.description,
				});
			}
		}
	};

	const defaultEditorProps: EditorProps = {
		// grapesjs: window.grapesjs,
		grapesjs: grapesjs,
		// grapesjsCss: 'http://localhost:8080/dist/css/grapes.min.css',
		grapesjsCss: "https://unpkg.com/grapesjs/dist/css/grapes.min.css",
		plugins: plugins,
		options: defaultOptions,
		onChange: (props) => {
			console.log("Changes", props);
		},
		onUpdate: (props) => {
			console.log("Update", props);
		},
	};

	saveThemeGlobal.prototype = {
		buttonText: "Add To Global",
	};

	return (
		<>
			<CustomDesignedEditor
				{...defaultEditorProps}
				onEditor={onEditor}
				publishPage={themeUpdate ? updateThemeGlobal : publishPage}
				updateThemeInfo={
					themeUpdate && isSuperShop(shop)
						? updateThemeInfo
						: undefined
				}
				page={
					header
						? ({
								name: header.name,
								_id: header._id,
						  } as any)
						: undefined
				}
				saveThemeGlobal={
					isSuperShop(shop)
						? themeUpdate
							? updateThemeGlobal
							: saveThemeGlobal
						: undefined
				}
				addBlockToRoot={addBlockToRootLayout}
				filteredCategories={[
					"basic",
					"sections",
					"header layouts",
					"search bar",
					"header components",
					"social buttons",
				]}
			/>
		</>
	);

	// return (
	// 	<div>
	// 		<button className="btn !w-auto mb-2 px-3 mr-3" onClick={exportJson}>
	// 			Export as JSON
	// 		</button>
	// 		<button className="btn !w-auto mb-2 px-3" onClick={publishPage}>
	// 			Publish
	// 		</button>
	// 		{/* <DefaultEditor {...defaultEditorProps} onEditor={onEditor} /> */}

	// 		<CustomDesignedEditor {...defaultEditorProps} onEditor={onEditor} />
	// 	</div>
	// );
};

export default HeaderBuilder;

const SaveThemePupup = ({
	handleClose,
	handleSubmit,

	image: _image,
	name,
	description,
}: {
	handleClose: () => void;
	handleSubmit: (data: {
		name: string;
		image: string;
		description?: string;
	}) => void;

	image?: string;
	name?: string;
	description?: string;
}) => {
	const dispatch = useDispatch();
	const [image, setImage] = useState<string>(_image || "");

	const handleUpload = () => {
		dispatch(
			setGlobalProductPopupElement(
				<ImagePopUpV3
					onClose={() => {
						dispatch(setGlobalProductPopupElement(null));
					}}
					onUpdate={(images) => {
						setImage(images?.[0]?.path || "");
						dispatch(setGlobalProductPopupElement(null));
					}}
					single
				/>
			)
		);
	};

	return (
		<Dialog
			open={true}
			onClose={handleClose}
			PaperProps={{
				component: "form",
				sx: {
					minWidth: "400px",
				},
				onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
					event.preventDefault();
					const formData = new FormData(event.currentTarget);
					const formJson = Object.fromEntries(
						(formData as any).entries()
					);
					const name = formJson.name;
					const description = formJson.description;
					handleSubmit({ name, image, description });
					handleClose();
				},
			}}>
			<DialogContent>
				<DialogContentText>
					Please enter theme name for the theme you are creating for
					global themes.
				</DialogContentText>
				<div className="w-full flex items-center justify-center p-4">
					<button
						type="button"
						onClick={handleUpload}
						className="border border-dashed border-gray-300 rounded-md p-4 text-center">
						<p className="text-sm font-light tracking-wide uppercase text-slate-500">
							Preview Banner
						</p>
						{!!image ? (
							<img
								src={getImageUrl(image)}
								alt=""
								className="w-[200px] h-[200px] object-contain"
							/>
						) : (
							<ImageArea
								style={{
									fontSize: "5rem",
								}}
							/>
						)}
					</button>
				</div>
				<TextField
					autoFocus
					required
					margin="dense"
					id="name"
					name="name"
					defaultValue={name || ""}
					label="Theme Name"
					type="text"
					fullWidth
					variant="standard"
				/>

				<TextField
					autoFocus
					margin="dense"
					id="description"
					defaultValue={description || ""}
					name="description"
					label="Description (Optional)"
					type="text"
					fullWidth
					variant="standard"
				/>
			</DialogContent>
			<DialogActions>
				<Button type="button" onClick={handleClose}>
					Cancel
				</Button>
				<Button variant="contained" type="submit">
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
